import { mergeSxProps, Text } from "@clipboard-health/ui-components";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type ReactElement, type ReactNode } from "react";

import {
  type CreditProgressBarMode,
  useProgressBarAnimation,
} from "../hooks/useProgressBarAnimation";
import { ShieldIcon } from "./ShieldIcon";

const CREDIT_PROGRESS_BAR_COLORS = {
  blue: {
    filledBarBackground:
      "radial-gradient(166.7% 163.74% at -2.78% -4.17%, #6E7FAE 10%, #4B69A5 48.5%, #596DA3 100%)",
    filledCircleBackground: "linear-gradient(135deg, #4B69A5 0%, #596DA3 100%)",
  },
  green: {
    filledBarBackground:
      "radial-gradient(166.7% 163.74% at -2.78% -4.17%, #43902E 10%, #3F7D28 48.5%, #3B6A21 100%)",
    filledCircleBackground: "linear-gradient(135deg, #3F7D28 0%, #3B6A21 100%)",
  },
} as const;

export type CreditProgressBarColor = keyof typeof CREDIT_PROGRESS_BAR_COLORS;

export interface CreditProgressBarProps {
  /**
   * Number of shifts completed towards the next credit
   */
  completedCount: number;
  /**
   * Number of shifts required to earn the next credit
   */
  requiredCount: number;
  /**
   * Optional label text displayed inside the progress bar
   * If not provided, no label will be shown
   */
  label?: string;
  /**
   * Color variant for the progress bar
   * @default "blue"
   */
  color?: CreditProgressBarColor;
  /**
   * Custom icon to display on the right side
   * @default ShieldIcon
   */
  icon?: ReactNode;
  /**
   * Controls how the progress bar animates:
   * - 'fill': animates from empty → current progress (earning behavior)
   * - 'drain': starts pre-loaded (full) and animates right-to-left to current progress (spending behavior)
   * - 'static': no animation, renders immediately at current progress
   * @default "static"
   */
  mode?: CreditProgressBarMode;
  /**
   * Custom sx prop for the container
   */
  sx?: SxProps<Theme>;
}

// The progress bar pill is curved only on the left side.
const BAR_HEIGHT = 24;
const BAR_BORDER_RADIUS = `${BAR_HEIGHT / 2}px 0 0 ${BAR_HEIGHT / 2}px`;
// The icon circle overlaps the end of the progress bar slightly, with a visual gap.
const CIRCLE_SIZE = 52;
const CIRCLE_BORDER_WIDTH = 3;
const ICON_SIZE = 24;
// Horizontal distance the circle overlaps the pill, measured from the pill's
// right edge to where the circle's arc meets the pill's top/bottom edges.
// The pill ends here so that "100% fill" = "visually full."
const CIRCLE_OVERLAP = Math.sqrt((CIRCLE_SIZE / 2) ** 2 - (BAR_HEIGHT / 2) ** 2);

export function CreditProgressBar(props: CreditProgressBarProps): ReactElement {
  const { completedCount, requiredCount, label, color = "blue", mode = "static", icon, sx } = props;

  const colorConfig = CREDIT_PROGRESS_BAR_COLORS[color];
  const {
    currentProgress,
    isCurrentProgressFull,
    isIconLit,
    barTransitionProps,
    iconTransitionProps,
  } = useProgressBarAnimation({ completedCount, requiredCount, mode });

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        },
        sx
      )}
    >
      {/* Progress bar container */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Pill — ends where the circle fully covers it, so 100% fill = visually full */}
        <Box
          role="progressbar"
          aria-valuenow={completedCount}
          aria-valuemin={0}
          aria-valuemax={requiredCount}
          aria-label={label}
          sx={(theme) => ({
            display: "flex",
            flex: 1,
            height: BAR_HEIGHT,
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            borderRadius: BAR_BORDER_RADIUS,
            overflow: "hidden",
            backgroundColor: theme.palette.background.tertiary,
            paddingLeft: 2,
            paddingRight: 4,
            zIndex: 0,
          })}
        >
          {/* Progress fill */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${currentProgress * 100}%`,
              background: colorConfig.filledBarBackground,
              borderRadius: BAR_BORDER_RADIUS,
              transitionProperty: "width",
              ...barTransitionProps,
            }}
          />

          {/* Dark label text (visible over light background) */}
          {label && (
            <Text
              semibold
              color={(theme) => theme.palette.text.primary}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                textAlign: "center",
                zIndex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingX: 2,
              }}
              variant="caption"
            >
              {label}
            </Text>
          )}

          {/* White label text (clipped to progress width using clip-path) */}
          {label && (
            <Text
              aria-hidden
              semibold
              color={(theme) => theme.palette.text.invertPrimary}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                textAlign: "center",
                zIndex: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingX: 2,
                // Clip from the right to match the progress fill width
                clipPath: `inset(0 ${(1 - currentProgress) * 100}% 0 0)`,
                transitionProperty: "clip-path",
                ...barTransitionProps,
              }}
              variant="caption"
            >
              {label}
            </Text>
          )}
        </Box>

        {/* Circular icon container */}
        <Box
          sx={(theme) => ({
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            background: theme.palette.background.tertiary,
            // Subtle shadow only when full
            boxShadow: isCurrentProgressFull ? "0px 2px 4px rgba(0, 0, 0, 0.1)" : "none",
            // Border matches card background to create the visual gap
            border: `${CIRCLE_BORDER_WIDTH}px solid ${theme.palette.background.primary}`,
            // Overlap the pill; the remaining visible overlap is the two slivers
            // above/below the arc, covered by the circle at z-index 1.
            marginLeft: `-${CIRCLE_SIZE / 2 - CIRCLE_OVERLAP}px`,
            zIndex: 1,
            position: "relative",
            overflow: "hidden",
            transitionProperty: "boxShadow",
            ...iconTransitionProps,
          })}
        >
          {/* Colored background overlay that fills from left to right (or drains right to left) */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: colorConfig.filledCircleBackground,
              borderRadius: "50%",
              // Clip from right, revealing from left as animation progresses
              clipPath: isCurrentProgressFull ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
              transitionProperty: "clip-path",
              ...iconTransitionProps,
            }}
          />
          {icon ?? (
            <ShieldIcon
              variant="outlined"
              sx={(theme) => ({
                width: ICON_SIZE,
                height: ICON_SIZE,
                color: isIconLit ? "white" : theme.palette.border?.primary,
                position: "relative",
                zIndex: 1,
                transitionProperty: "color",
                transitionDuration: iconTransitionProps.transitionDuration,
                transitionTimingFunction: iconTransitionProps.transitionTimingFunction,
              })}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
